/**
 * ListingsDrawer component
 *
 * Right-side resizable drawer for listing display.
 * Positioned below the CNTL drawer, with coordinated open/close behavior.
 * No tabs or header - just shows filtered listings.
 * Handle pulls out with the drawer.
 */
import { formatNeighborhoodName, formatListingAddress, isNewListing, isListedToday, listingItemKey, factsFromListingSummary } from '@slowmaui/shared-types';
import { ATTRIBUTE_REGISTRY } from '../attributes/registry.js';
import { formatRelativeDate } from '../formatRelativeDate.js';
import { tourLabel } from '../tourUrl.js';
import { injectStyles, MIN_CARD_COL_WIDTH, CARD_GRID_GAP } from './styles.js';
function escapeAttr(s) {
    return String(s).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}
// Cap on the flat (non-grouped) sort views - unlike the neighborhood-grouped
// view, a flat sort has no natural collapse point, so at the default
// zoomed-out view (up to ~2000 listings island-wide) it built a full-card DOM
// node per listing with no limit at all. 200 is generous for actually
// browsing a list; anyone with more results is better served narrowing
// filters or zooming in than scrolling past hundreds of cards anyway.
const FLAT_LIST_RENDER_CAP = 200;
export class ListingsDrawer {
    container;
    listings = [];
    // True while the map already has bubbles from the fast tmk_status path
    // but the richer per-listing data this drawer needs (address, photos,
    // exact beds/baths) hasn't loaded yet - see loadListingsForDrawer in
    // apps/public/src/main.ts. Map bubbles are intentionally NOT gated on
    // this; the drawer alone shows a loading state.
    loading = false;
    onListingClick;
    onListingHover;
    onNeighborhoodHeaderClick;
    onFavoriteToggle;
    onListingAlertToggle;
    onNeighborhoodFavoriteToggle;
    onNeighborhoodAlertToggle;
    onAddToCalendar;
    extraHeaderButton;
    agentPicksMenu;
    groupByNeighborhood = false;
    favoriteIds = new Set();
    alertIds = new Set();
    agentPickIds = new Set();
    featuredIds = new Set();
    activeAttributes = Object.fromEntries(ATTRIBUTE_REGISTRY.map((entry) => [entry.id, false]));
    sectionExpandState = Object.fromEntries(ATTRIBUTE_REGISTRY.map((entry) => [entry.id, true]));
    neighborhoodExpandState = {};
    sortOption = 'neighborhood';
    filtersActive = false;
    cachedNeighborhoodCount = 0;
    updateDebounceTimer = null;
    previousFavoriteIds = new Set();
    previousAlertIds = new Set();
    constructor(options) {
        injectStyles();
        this.container = options.container;
        this.onListingClick = options.onListingClick;
        this.onListingHover = options.onListingHover;
        this.onNeighborhoodHeaderClick = options.onNeighborhoodHeaderClick;
        this.onFavoriteToggle = options.onFavoriteToggle;
        this.onListingAlertToggle = options.onListingAlertToggle;
        this.onNeighborhoodFavoriteToggle = options.onNeighborhoodFavoriteToggle;
        this.onNeighborhoodAlertToggle = options.onNeighborhoodAlertToggle;
        this.onAddToCalendar = options.onAddToCalendar;
        this.extraHeaderButton = options.extraHeaderButton;
        this.agentPicksMenu = options.agentPicksMenu;
        this.groupByNeighborhood = options.groupByNeighborhood ?? false;
        this.favoriteIds = options.favoriteIds || new Set();
        this.alertIds = options.alertIds || new Set();
        this.agentPickIds = options.agentPickIds || new Set();
        this.featuredIds = options.featuredIds || new Set();
        if (options.activeAttributes) {
            this.activeAttributes = { ...options.activeAttributes };
        }
        this.render();
        this.attachEventListeners();
    }
    render() {
        // Always renders inline into the host container (UnifiedDrawer's listings slot).
        this.container.innerHTML = `
      <div class="listings-embedded" id="listings-drawer-component">
        <div class="listings-drawer-body" id="listings-drawer-body">
          ${this.renderListings()}
        </div>
      </div>
    `;
    }
    hasAnyActiveAttribute() {
        return ATTRIBUTE_REGISTRY.some((entry) => this.activeAttributes[entry.id]);
    }
    renderListings() {
        if (this.loading) {
            return '<div class="listings-drawer-loading"><div class="listings-drawer-spinner"></div>Loading listings...</div>';
        }
        if (this.listings.length === 0) {
            return '<div class="listings-drawer-empty">No listings to display</div>';
        }
        // Always show sort header
        const sortHeader = this.renderSortHeader();
        if (this.hasAnyActiveAttribute()) {
            return sortHeader + this.renderAttributeSections();
        }
        // "Neighborhood" sort = grouped view, any other sort = flat list
        if (this.sortOption === 'neighborhood') {
            return sortHeader + this.renderGroupedListings();
        }
        // Flat list with sorting
        const sorted = this.sortListings(this.listings);
        const capped = sorted.slice(0, FLAT_LIST_RENDER_CAP);
        const cards = capped.map((listing) => this.renderListingCard(listing)).join('');
        const overflowNotice = sorted.length > FLAT_LIST_RENDER_CAP
            ? `<div class="listings-drawer-overflow-notice">Showing ${FLAT_LIST_RENDER_CAP} of ${sorted.length} - narrow your filters or zoom in to see more</div>`
            : '';
        return sortHeader + `<div class="listings-card-grid">${cards}</div>` + overflowNotice;
    }
    /** Empty string if no agentPicksMenu was configured, or there's no
     *  history yet to switch between - a brand-new visitor with no past
     *  Agent Picks link opened sees nothing here, same as before. */
    renderAgentPicksSelect() {
        if (!this.agentPicksMenu)
            return '';
        const history = this.agentPicksMenu.getHistory();
        if (history.length === 0)
            return '';
        const options = history
            .map((h) => `<option value="${escapeAttr(h.token)}"${h.isActive ? ' selected' : ''}>${escapeAttr(h.label)}</option>`)
            .join('');
        const noneActive = !history.some((h) => h.isActive);
        return `
      <select class="listings-agent-picks-select" id="listings-agent-picks-select" title="Agent Picks">
        <option value=""${noneActive ? ' selected' : ''}>Current View</option>
        ${options}
      </select>
    `;
    }
    /**
     * Render the sort header bar (always visible)
     */
    renderSortHeader() {
        const filtersIndicator = this.filtersActive
            ? '<span class="listings-filters-indicator on">Filtered</span>'
            : '';
        const extraBtn = this.extraHeaderButton
            ? `<button class="listings-extra-btn" id="listings-extra-btn"
                 title="${escapeAttr(this.extraHeaderButton.title ?? '')}">${escapeAttr(this.extraHeaderButton.label)}</button>`
            : '';
        return `
      <div class="listings-sort-header">
        <span class="listings-sort-label">${this.listings.length} listing${this.listings.length !== 1 ? 's' : ''}</span>
        <div class="listings-sort-controls">
          ${filtersIndicator}
          ${extraBtn}
          ${this.renderAgentPicksSelect()}
          <select class="listings-sort-dropdown" id="listings-sort">
            <option value="neighborhood"${this.sortOption === 'neighborhood' ? ' selected' : ''}>Neighborhood</option>
            <option value="price-high"${this.sortOption === 'price-high' ? ' selected' : ''}>Price: High to Low</option>
            <option value="price-low"${this.sortOption === 'price-low' ? ' selected' : ''}>Price: Low to High</option>
            <option value="newest"${this.sortOption === 'newest' ? ' selected' : ''}>Newest First</option>
            <option value="dom"${this.sortOption === 'dom' ? ' selected' : ''}>Days on Market</option>
            <option value="sqft"${this.sortOption === 'sqft' ? ' selected' : ''}>Largest (sqft)</option>
            <option value="beds"${this.sortOption === 'beds' ? ' selected' : ''}>Most Bedrooms</option>
          </select>
        </div>
      </div>
    `;
    }
    /**
     * How many MIN_CARD_COL_WIDTH-wide columns currently fit the drawer body -
     * used to size a collapsed neighborhood group's preview (show a full row
     * of cards, not a hardcoded single one) so the multi-column layout is
     * actually visible without expanding every group by hand. Matches
     * .listings-card-grid's own CSS formula so the two can't disagree; falls
     * back to 1 when the container isn't laid out yet (e.g. mid-transition
     * while the drawer is opening).
     */
    computeColumnCount() {
        const width = this.container.clientWidth;
        const usable = width - 24; // .listings-drawer-body's 12px padding each side
        if (usable < MIN_CARD_COL_WIDTH)
            return 1;
        return Math.max(1, Math.floor((usable + CARD_GRID_GAP) / (MIN_CARD_COL_WIDTH + CARD_GRID_GAP)));
    }
    renderGroupedListings() {
        // Group listings by neighborhood
        const grouped = this.groupListingsByNeighborhood(this.listings);
        // Sort groups: neighborhoods first (alphabetically), then "Other Properties"
        const sortedGroups = [...grouped.entries()].sort((a, b) => {
            // "Other" goes last
            if (a[0].id === '__other__')
                return 1;
            if (b[0].id === '__other__')
                return -1;
            // Alphabetical by name
            return a[0].name.localeCompare(b[0].name);
        });
        // Cache the count for use in other methods
        this.cachedNeighborhoodCount = sortedGroups.length;
        // When there are many neighborhoods (>3), default to collapsed for better UX
        const manyNeighborhoods = sortedGroups.length > 3;
        // Computed once per render, not per group - it's a property of the
        // drawer's current width, not of any individual neighborhood.
        const columnCount = this.computeColumnCount();
        return sortedGroups.map(([neighborhood, listings]) => {
            // Check explicit expand state, otherwise use smart default
            const explicitState = this.neighborhoodExpandState[neighborhood.id];
            const isExpanded = explicitState !== undefined
                ? explicitState
                : !manyNeighborhoods; // Default expanded if few neighborhoods, collapsed if many
            const header = this.renderGroupHeader(neighborhood, listings.length, manyNeighborhoods, columnCount);
            // Sort listings within each neighborhood
            const sortedListings = this.sortListings(listings);
            if (isExpanded) {
                // Fully expanded: show all listings
                const cards = sortedListings.map(l => this.renderListingCard(l, false)).join('');
                return `
          <div class="listings-group" data-neighborhood-id="${neighborhood.id}">
            ${header}
            <div class="listings-group-content">
              ${cards}
            </div>
          </div>
        `;
            }
            else {
                // Collapsed with preview: show a full row's worth of cards (however
                // many columns currently fit), not a hardcoded single one - lets
                // the multi-column layout actually show without expanding every
                // group by hand.
                const previewCards = sortedListings.slice(0, columnCount).map(l => this.renderListingCard(l, false)).join('');
                return `
          <div class="listings-group" data-neighborhood-id="${neighborhood.id}">
            ${header}
            <div class="listings-group-content listings-group-preview">
              ${previewCards}
            </div>
          </div>
        `;
            }
        }).join('');
    }
    /**
     * Render attribute-based sections (Favorites, Alerts, New, Open House, All Listings)
     */
    renderAttributeSections() {
        const idSets = { favorites: this.favoriteIds, alerts: this.alertIds, agentPicks: this.agentPickIds, featured: this.featuredIds };
        // Unique neighborhoods present in the current listing set, for resolving
        // nh:-prefixed favorite/alert ids (id-set attributes only) to a name/count.
        const neighborhoodMap = new Map();
        for (const listing of this.listings) {
            if (listing.neighborhoodId && listing.neighborhoodName) {
                neighborhoodMap.set(listing.neighborhoodId, listing.neighborhoodName);
            }
        }
        // Track which items are shown in sections
        const shownInSections = new Set();
        // Build sections, in registry priority order (Favorites, Alerts, New,
        // Price Change, Open House).
        const sections = [];
        for (const entry of ATTRIBUTE_REGISTRY) {
            if (!this.activeAttributes[entry.id])
                continue;
            // Sections previously ignored the dropdown's sortOption entirely and
            // rendered in raw filter order regardless of what the user picked -
            // Favorites/Alerts (no dedicated sortCompare) fall through to it here;
            // New/Price Change/Open House keep their own recency sorts.
            const matchedListings = this.listings.filter((l) => entry.matches(factsFromListingSummary(l), idSets));
            const sortedListings = entry.sortCompare
                ? [...matchedListings].sort(entry.sortCompare)
                : this.sortListings(matchedListings);
            // Favorites/Alerts (id-set attributes) also surface whole favorited/
            // alerted neighborhoods (nh:-prefixed ids) as their own compact rows -
            // no data-predicate attribute has a "whole neighborhood matches" concept.
            const neighborhoodRows = [];
            if (entry.kind === 'id-set') {
                const nhIds = new Set();
                for (const id of idSets[entry.id] ?? []) {
                    if (id.startsWith('nh:'))
                        nhIds.add(id.substring(3));
                }
                for (const nhId of nhIds) {
                    const name = neighborhoodMap.get(nhId);
                    if (name) {
                        const count = this.listings.filter((l) => l.neighborhoodId === nhId).length;
                        neighborhoodRows.push({ id: nhId, name, count });
                    }
                }
            }
            if (sortedListings.length === 0 && neighborhoodRows.length === 0)
                continue;
            const isExpanded = this.sectionExpandState[entry.id] !== false;
            const totalCount = sortedListings.length + neighborhoodRows.length;
            // Neighborhood rows stay outside the card grid - they're a compact
            // horizontal summary row, not a listing card, and shouldn't get
            // squeezed into a narrower grid column.
            let rowsHtml = '';
            for (const nh of neighborhoodRows) {
                rowsHtml += this.renderNeighborhoodRow(nh);
            }
            let cardsHtml = '';
            for (const listing of sortedListings) {
                if (!shownInSections.has(listing.id)) {
                    cardsHtml += this.renderListingCard(listing);
                    shownInSections.add(listing.id);
                }
            }
            const content = rowsHtml + (cardsHtml ? `<div class="listings-card-grid">${cardsHtml}</div>` : '');
            if (content) {
                sections.push(this.renderSection(entry.id, entry.label, totalCount, isExpanded, content));
            }
        }
        // All Listings section (remaining items not shown in attribute sections)
        const remainingListings = this.listings.filter(l => !shownInSections.has(l.id));
        if (remainingListings.length > 0) {
            // Sort remaining listings
            const sortedListings = this.sortListings(remainingListings);
            let content;
            if (this.groupByNeighborhood) {
                // Group by neighborhood when enabled
                content = this.renderGroupedListingsContent(sortedListings);
            }
            else {
                const cardsHtml = sortedListings.map(l => this.renderListingCard(l)).join('');
                content = `<div class="listings-card-grid">${cardsHtml}</div>`;
            }
            sections.push(this.renderSection('all', 'Listings', remainingListings.length, true, content, true));
        }
        return sections.join('');
    }
    /**
     * Sort listings based on current sort option
     */
    sortListings(listings) {
        const sorted = [...listings];
        switch (this.sortOption) {
            case 'price-high':
                sorted.sort((a, b) => (b.listPrice || 0) - (a.listPrice || 0));
                break;
            case 'price-low':
                sorted.sort((a, b) => (a.listPrice || 0) - (b.listPrice || 0));
                break;
            case 'newest':
                sorted.sort((a, b) => {
                    const dateA = a.listDate ? new Date(a.listDate).getTime() : 0;
                    const dateB = b.listDate ? new Date(b.listDate).getTime() : 0;
                    return dateB - dateA;
                });
                break;
            case 'dom':
                sorted.sort((a, b) => (b.daysOnMarket || 0) - (a.daysOnMarket || 0));
                break;
            case 'sqft':
                sorted.sort((a, b) => (b.livingArea || 0) - (a.livingArea || 0));
                break;
            case 'beds':
                sorted.sort((a, b) => (b.bedrooms || 0) - (a.bedrooms || 0));
                break;
            case 'neighborhood':
                // The main (non-attribute) view handles 'neighborhood' by routing
                // to a fully grouped-with-headers render instead of calling
                // sortListings at all - this case only matters for callers (the
                // attribute sections' Favorites/Alerts/New lists) that render a
                // flat list regardless of sort option. Sorting alphabetically at
                // least clusters same-neighborhood cards together in sequence
                // rather than leaving raw filter order, even without a visible
                // group header - renderListingCard shows the neighborhood name on
                // the card itself in these flat contexts.
                sorted.sort((a, b) => (a.neighborhoodName || '￿').localeCompare(b.neighborhoodName || '￿'));
                break;
        }
        return sorted;
    }
    /** Parses "11:00 AM" / "1:30 PM" style strings into 24-hour hours/minutes. */
    parseOpenHouseTime(timeStr) {
        if (!timeStr)
            return null;
        const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (!match)
            return null;
        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const period = match[3].toUpperCase();
        if (period === 'PM' && hours !== 12)
            hours += 12;
        if (period === 'AM' && hours === 12)
            hours = 0;
        return { hours, minutes };
    }
    /** Formats a parsed open house time compactly - "1PM", "1:30PM" (only shows minutes when non-zero). */
    formatOpenHouseTimeShort(timeStr) {
        const parsed = this.parseOpenHouseTime(timeStr);
        if (!parsed)
            return null;
        const { hours, minutes } = parsed;
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHour = hours % 12 === 0 ? 12 : hours % 12;
        return minutes === 0 ? `${displayHour}${period}` : `${displayHour}:${minutes.toString().padStart(2, '0')}${period}`;
    }
    /**
     * Render listings grouped by neighborhood (for All Listings section)
     *
     * Mirrors renderGroupedListings' smart-default collapse (>3 neighborhoods
     * defaults to collapsed-with-preview): this used to always build every
     * group's full card list and only hide the rest via a `collapsed` CSS
     * class, so an attribute-filtered "All Listings" section at the default
     * zoomed-out view (many neighborhoods) still built a full-card DOM node
     * per listing - up to ~2000 at once - regardless of what was actually
     * visible. Confirmed live 2026-09 as a real contributor to mobile jank.
     */
    renderGroupedListingsContent(listings) {
        const grouped = this.groupListingsByNeighborhood(listings);
        // Sort groups: neighborhoods first (alphabetically), then "Other Properties"
        const sortedGroups = [...grouped.entries()].sort((a, b) => {
            if (a[0].id === '__other__')
                return 1;
            if (b[0].id === '__other__')
                return -1;
            return a[0].name.localeCompare(b[0].name);
        });
        const manyNeighborhoods = sortedGroups.length > 3;
        const columnCount = this.computeColumnCount();
        return sortedGroups.map(([neighborhood, nhListings]) => {
            const explicitState = this.neighborhoodExpandState[neighborhood.id];
            const isExpanded = explicitState !== undefined ? explicitState : !manyNeighborhoods;
            const header = this.renderGroupHeader(neighborhood, nhListings.length, manyNeighborhoods, columnCount);
            if (isExpanded) {
                const cards = nhListings.map(l => this.renderListingCard(l, false)).join('');
                return `
          <div class="listings-group" data-neighborhood-id="${neighborhood.id}">
            ${header}
            <div class="listings-group-content">
              ${cards}
            </div>
          </div>
        `;
            }
            const previewCards = nhListings.slice(0, columnCount).map(l => this.renderListingCard(l, false)).join('');
            return `
        <div class="listings-group" data-neighborhood-id="${neighborhood.id}">
          ${header}
          <div class="listings-group-content listings-group-preview">
            ${previewCards}
          </div>
        </div>
      `;
        }).join('');
    }
    /**
     * Render a section header and content
     */
    renderSection(sectionId, name, count, isExpanded, content, isAllListings = false) {
        const chevronSvg = '<svg class="listings-section-chevron' + (isExpanded ? ' expanded' : '') + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>';
        return `
      <div class="listings-attribute-section" data-section="${sectionId}">
        <div class="listings-section-header section-${sectionId}${isAllListings ? ' all-listings' : ''}" data-section="${sectionId}">
          <div class="listings-section-header-left">
            ${isAllListings ? '' : chevronSvg}
            <span class="listings-section-name">${name}</span>
            <span class="listings-section-count">${count}</span>
          </div>
        </div>
        <div class="listings-section-content${isExpanded ? '' : ' collapsed'}">
          ${content}
        </div>
      </div>
    `;
    }
    /**
     * Render an expandable neighborhood row for attribute sections
     * Shows listings when expanded, with stacked heart/bell icons like properties
     */
    renderNeighborhoodRow(neighborhood) {
        const isFavorite = this.favoriteIds.has(`nh:${neighborhood.id}`);
        const hasAlert = this.alertIds.has(`nh:${neighborhood.id}`);
        const isExpanded = this.neighborhoodExpandState[`attr:${neighborhood.id}`] === true; // Default to collapsed
        const chevronSvg = '<svg class="listings-nh-row-chevron' + (isExpanded ? ' expanded' : '') + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>';
        const heartIcon = isFavorite
            ? '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>';
        const bellIcon = hasAlert
            ? '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/></svg>';
        // Get listings for this neighborhood to show when expanded
        const nhListings = this.listings.filter(l => l.neighborhoodId === neighborhood.id);
        const listingsHtml = isExpanded
            ? nhListings.map(l => this.renderListingCard(l, false)).join('')
            : '';
        return `
      <div class="listings-neighborhood-row-wrapper" data-neighborhood-id="${neighborhood.id}">
        <div class="listings-neighborhood-row" data-neighborhood-id="${neighborhood.id}">
          <div class="listings-nh-row-left">
            ${chevronSvg}
            <span class="listings-neighborhood-row-name">${formatNeighborhoodName(neighborhood.name)}</span>
            <span class="listings-neighborhood-row-count">${neighborhood.count} for sale</span>
          </div>
          <div class="listings-nh-row-actions-stacked">
            <button class="listings-nh-heart${isFavorite ? ' is-favorite' : ''}" data-neighborhood-id="${neighborhood.id}" title="${isFavorite ? 'Remove from favorites' : 'Add to favorites'}">
              ${heartIcon}
            </button>
            <button class="listings-nh-bell${hasAlert ? ' has-alert' : ''}" data-neighborhood-id="${neighborhood.id}" title="${hasAlert ? 'Remove alert' : 'Set alert'}">
              ${bellIcon}
            </button>
          </div>
        </div>
        <div class="listings-nh-row-content${isExpanded ? '' : ' collapsed'}">
          ${listingsHtml}
        </div>
      </div>
    `;
    }
    groupListingsByNeighborhood(listings) {
        const grouped = new Map();
        for (const listing of listings) {
            const id = listing.neighborhoodId || '__other__';
            const name = listing.neighborhoodName || 'Other Properties';
            if (!grouped.has(id)) {
                grouped.set(id, { neighborhood: { id, name }, listings: [] });
            }
            grouped.get(id).listings.push(listing);
        }
        // Convert to the expected Map format
        const result = new Map();
        for (const { neighborhood, listings } of grouped.values()) {
            result.set(neighborhood, listings);
        }
        return result;
    }
    renderGroupHeader(neighborhood, count, manyNeighborhoods, previewCount = 1) {
        const isOther = neighborhood.id === '__other__';
        // Use smart default: collapsed when many neighborhoods (>3), expanded when few
        const useManyNeighborhoods = manyNeighborhoods ?? this.cachedNeighborhoodCount > 3;
        const explicitState = this.neighborhoodExpandState[neighborhood.id];
        const isExpanded = explicitState !== undefined ? explicitState : !useManyNeighborhoods;
        const isFavorite = this.favoriteIds.has(`nh:${neighborhood.id}`);
        const hasAlert = this.alertIds.has(`nh:${neighborhood.id}`);
        const chevronSvg = '<svg class="listings-group-chevron' + (isExpanded ? ' expanded' : '') + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>';
        const heartIcon = isFavorite
            ? '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>';
        const bellIcon = hasAlert
            ? '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/></svg>';
        // "Show All" link appears when collapsed and there are more listings
        // than the preview row already shows (previewCount, not a flat 1 -
        // see computeColumnCount).
        const showAllHtml = !isExpanded && count > previewCount
            ? '<span class="listings-show-all">Show All</span>'
            : '';
        const displayName = formatNeighborhoodName(neighborhood.name);
        if (isOther) {
            return `
        <div class="listings-group-header other${isExpanded ? '' : ' collapsed-preview'}" data-neighborhood-id="${neighborhood.id}" data-neighborhood-name="${displayName}">
          <div class="listings-group-header-left">
            ${chevronSvg}
            <span class="listings-group-name">${displayName}</span>
          </div>
          <div class="listings-group-header-right">
            ${showAllHtml}
            <span class="listings-group-count">${count}</span>
          </div>
        </div>
      `;
        }
        return `
      <div class="listings-group-header${isExpanded ? '' : ' collapsed-preview'}" data-neighborhood-id="${neighborhood.id}" data-neighborhood-name="${displayName}">
        <div class="listings-group-header-left">
          ${chevronSvg}
          <span class="listings-group-name">${displayName}</span>
        </div>
        <div class="listings-group-header-right">
          ${showAllHtml}
          <span class="listings-group-count">${count}</span>
          <button class="listings-nh-heart${isFavorite ? ' is-favorite' : ''}" data-neighborhood-id="${neighborhood.id}" title="${isFavorite ? 'Remove from favorites' : 'Add to favorites'}">
            ${heartIcon}
          </button>
          <button class="listings-nh-bell${hasAlert ? ' has-alert' : ''}" data-neighborhood-id="${neighborhood.id}" title="${hasAlert ? 'Remove alert' : 'Set alert'}">
            ${bellIcon}
          </button>
        </div>
      </div>
    `;
    }
    // showNeighborhood defaults true - card call sites already nested under a
    // neighborhood group header (renderGroupedListings/Content) pass false,
    // since the header right above already says it. Every other context
    // (flat sort views, the attribute sections' Favorites/Alerts/New/Open
    // House lists) has no such header, so the name goes on the card itself -
    // useful in any sort mode, not just when sorted by neighborhood, and
    // avoids adding neighborhood-grouping headers to sections that are
    // usually too short for them to earn their visual weight.
    renderListingCard(listing, showNeighborhood = true) {
        const price = this.formatPrice(listing.listPrice);
        const photo = listing.photoUrl || listing.primaryPhotoUrl;
        const tmk = listingItemKey(listing);
        const isFavorite = this.favoriteIds.has(tmk);
        const hasAlert = this.alertIds.has(tmk);
        const hasOpenHouse = !!listing.openHouse;
        const imageHtml = photo
            ? `<img class="listing-card-image" src="${photo}" alt="Property photo" loading="lazy">`
            : `<div class="listing-card-image-placeholder"></div>`;
        // Collect badges for new, price change, and open house listings (shown inline with price)
        const badges = [];
        if (isNewListing(listing)) {
            badges.push(`<span class="listing-badge new">${isListedToday(listing) ? 'New Today' : 'New'}</span>`);
        }
        if (listing.hasPriceChange && typeof listing.previousPrice === 'number') {
            // Shows the actual delta rather than a bare label, and the real
            // direction (unlike the attribute-level icon, which stays neutral
            // since price changes aren't always down) - once it's known for this
            // specific listing there's no reason to hide it. Date included since
            // "Price Change" alone doesn't say whether that was today or six
            // days ago.
            const arrow = listing.listPrice > listing.previousPrice ? '&uarr;' : '&darr;';
            const dateSuffix = listing.priceChangeDate ? ` &middot; ${formatRelativeDate(listing.priceChangeDate)}` : '';
            badges.push(`<span class="listing-badge price-change">${arrow} ${this.formatPrice(listing.previousPrice)} &rarr; ${this.formatPrice(listing.listPrice)}${dateSuffix}</span>`);
        }
        if (listing.openHouse) {
            const oh = listing.openHouse;
            const dateStr = this.formatOpenHouseDate(oh.date);
            const startStr = this.formatOpenHouseTimeShort(oh.start);
            const endStr = this.formatOpenHouseTimeShort(oh.end);
            const timeRange = startStr && endStr ? `, ${startStr} to ${endStr}` : startStr ? `, ${startStr}` : '';
            badges.push(`<span class="listing-badge open-house">Open ${dateStr}${timeRange}</span>`);
        }
        if (listing.virtualTourUrl) {
            badges.push(`<span class="listing-badge virtual-tour">${tourLabel(listing.virtualTourUrl)}</span>`);
        }
        // Action buttons - heart, bell, calendar (for open house)
        const heartIcon = isFavorite
            ? '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>';
        const bellIcon = hasAlert
            ? '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/></svg>';
        const calendarIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>';
        return `
      <div class="listing-card" data-listing-id="${listing.id}" data-tmk="${tmk}">
        <div class="listing-card-image-container">
          ${imageHtml}
        </div>
        <div class="listing-card-info">
          <div class="listing-card-price-row">
            <span class="listing-card-price">${price}</span>
            ${badges.length > 0 ? `<span class="listing-card-badges">${badges.join('')}</span>` : ''}
          </div>
          <div class="listing-card-address">${formatListingAddress(listing)}</div>
          ${showNeighborhood && listing.neighborhoodName ? `<div class="listing-card-neighborhood">${formatNeighborhoodName(listing.neighborhoodName)}</div>` : ''}
          <div class="listing-card-stats">
            ${listing.bedrooms ? `<span class="listing-card-stat">${listing.bedrooms} bd</span>` : ''}
            ${listing.bathrooms ? `<span class="listing-card-stat">${listing.bathrooms} ba</span>` : ''}
            ${listing.livingArea ? `<span class="listing-card-stat">${listing.livingArea.toLocaleString()} sqft</span>` : ''}
          </div>
        </div>
        <div class="listing-card-actions">
          <button class="listing-action-btn listing-action-heart${isFavorite ? ' is-favorite' : ''}" data-tmk="${tmk}" title="${isFavorite ? 'Remove from favorites' : 'Add to favorites'}">
            ${heartIcon}
          </button>
          <button class="listing-action-btn listing-action-bell${hasAlert ? ' has-alert' : ''}" data-tmk="${tmk}" title="${hasAlert ? 'Remove alert' : 'Set alert'}">
            ${bellIcon}
          </button>
          ${hasOpenHouse ? `
          <button class="listing-action-btn listing-action-calendar" data-listing-id="${listing.id}" title="Add to calendar">
            ${calendarIcon}
          </button>
          ` : ''}
        </div>
      </div>
    `;
    }
    formatOpenHouseDate(dateStr) {
        try {
            const date = new Date(dateStr + 'T12:00:00');
            const today = new Date();
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            if (date.toDateString() === today.toDateString()) {
                return 'Today';
            }
            if (date.toDateString() === tomorrow.toDateString()) {
                return 'Tomorrow';
            }
            return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        }
        catch {
            return dateStr;
        }
    }
    formatPrice(price) {
        if (price >= 1000000) {
            return `$${(price / 1000000).toFixed(price % 1000000 === 0 ? 0 : 1)}M`;
        }
        return `$${Math.round(price / 1000)}K`;
    }
    updateBody() {
        const body = this.container.querySelector('#listings-drawer-body');
        if (body) {
            body.innerHTML = this.renderListings();
            this.attachCardListeners();
            // The sort header (and any extra header button/Agent Picks select) is
            // rebuilt as part of renderListings(), so its handlers need to be
            // re-bound here too — initial-render attach in the constructor only
            // covers the first paint, which has no listings (and therefore no
            // header at all).
            this.attachExtraHeaderButton();
            this.attachAgentPicksSelect();
        }
    }
    /**
     * Debounced update - prevents rapid rebuilds from multiple quick changes
     */
    debouncedUpdateBody() {
        if (this.updateDebounceTimer) {
            clearTimeout(this.updateDebounceTimer);
        }
        this.updateDebounceTimer = setTimeout(() => {
            this.updateBody();
            this.updateDebounceTimer = null;
        }, 100);
    }
    /**
     * Update just the favorite/alert icons on cards without rebuilding DOM.
     * Returns true if selective update was performed, false if full rebuild is needed.
     */
    updateCardIcons(changedTmks, type) {
        // If attribute sections are active, we need full rebuild because cards move between sections
        if (this.hasAnyActiveAttribute()) {
            return false; // Need full rebuild
        }
        // Selective update: just toggle icon states
        for (const tmk of changedTmks) {
            const cards = this.container.querySelectorAll(`[data-tmk="${tmk}"]`);
            cards.forEach(card => {
                if (type === 'favorite') {
                    const btn = card.querySelector('.listing-action-heart');
                    if (btn) {
                        const isFavorite = this.favoriteIds.has(tmk);
                        btn.classList.toggle('is-favorite', isFavorite);
                        btn.setAttribute('title', isFavorite ? 'Remove from favorites' : 'Add to favorites');
                        // Update SVG
                        const svg = btn.querySelector('svg');
                        if (svg) {
                            svg.setAttribute('fill', isFavorite ? 'currentColor' : 'none');
                        }
                    }
                }
                else {
                    const btn = card.querySelector('.listing-action-bell');
                    if (btn) {
                        const hasAlert = this.alertIds.has(tmk);
                        btn.classList.toggle('has-alert', hasAlert);
                        btn.setAttribute('title', hasAlert ? 'Remove alert' : 'Set alert');
                        // Update SVG
                        const svg = btn.querySelector('svg');
                        if (svg) {
                            svg.setAttribute('fill', hasAlert ? 'currentColor' : 'none');
                        }
                    }
                }
            });
            // Also update neighborhood header icons if this is a neighborhood favorite/alert
            if (tmk.startsWith('nh:')) {
                const nhId = tmk.substring(3);
                const nhBtns = this.container.querySelectorAll(type === 'favorite'
                    ? `.listings-nh-heart[data-neighborhood-id="${nhId}"]`
                    : `.listings-nh-bell[data-neighborhood-id="${nhId}"]`);
                nhBtns.forEach(btn => {
                    if (type === 'favorite') {
                        const isFavorite = this.favoriteIds.has(tmk);
                        btn.classList.toggle('is-favorite', isFavorite);
                        const svg = btn.querySelector('svg');
                        if (svg)
                            svg.setAttribute('fill', isFavorite ? 'currentColor' : 'none');
                    }
                    else {
                        const hasAlert = this.alertIds.has(tmk);
                        btn.classList.toggle('has-alert', hasAlert);
                        const svg = btn.querySelector('svg');
                        if (svg)
                            svg.setAttribute('fill', hasAlert ? 'currentColor' : 'none');
                    }
                });
            }
        }
        return true;
    }
    /**
     * Find which IDs changed between two sets
     */
    getChangedIds(oldSet, newSet) {
        const changed = new Set();
        // Added
        for (const id of newSet) {
            if (!oldSet.has(id))
                changed.add(id);
        }
        // Removed
        for (const id of oldSet) {
            if (!newSet.has(id))
                changed.add(id);
        }
        return changed;
    }
    attachEventListeners() {
        // Host (UnifiedDrawer) owns the handle and resize. Listings only wires its own cards.
        this.attachCardListeners();
        this.attachExtraHeaderButton();
        this.attachAgentPicksSelect();
    }
    attachExtraHeaderButton() {
        if (!this.extraHeaderButton)
            return;
        const btn = this.container.querySelector('#listings-extra-btn');
        btn?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.extraHeaderButton.onClick();
        });
    }
    attachAgentPicksSelect() {
        if (!this.agentPicksMenu)
            return;
        const select = this.container.querySelector('#listings-agent-picks-select');
        select?.addEventListener('change', (e) => {
            e.stopPropagation();
            this.agentPicksMenu.onSelect(select.value);
        });
    }
    attachCardListeners() {
        const cards = this.container.querySelectorAll('.listing-card');
        cards.forEach((card) => {
            const listingId = card.dataset.listingId;
            const listing = this.listings.find((l) => l.id === listingId);
            if (listing) {
                card.addEventListener('click', (e) => {
                    // Don't trigger if clicking any action button
                    if (e.target.closest('.listing-action-btn') ||
                        e.target.closest('.listing-card-actions')) {
                        return;
                    }
                    if (this.onListingClick) {
                        this.onListingClick(listing);
                    }
                });
                card.addEventListener('mouseenter', () => {
                    if (this.onListingHover) {
                        this.onListingHover(listing);
                    }
                });
                card.addEventListener('mouseleave', () => {
                    if (this.onListingHover) {
                        this.onListingHover(null);
                    }
                });
            }
        });
        // Attach favorite (heart) button click listeners
        const heartBtns = this.container.querySelectorAll('.listing-action-heart');
        heartBtns.forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const tmk = btn.dataset.tmk;
                if (tmk && this.onFavoriteToggle) {
                    this.onFavoriteToggle(tmk);
                }
            });
        });
        // Attach alert (bell) button click listeners
        const bellBtns = this.container.querySelectorAll('.listing-action-bell');
        bellBtns.forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const tmk = btn.dataset.tmk;
                if (tmk && this.onListingAlertToggle) {
                    this.onListingAlertToggle(tmk);
                }
            });
        });
        // Attach calendar button click listeners (for open house)
        const calendarBtns = this.container.querySelectorAll('.listing-action-calendar');
        calendarBtns.forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const listingId = btn.dataset.listingId;
                const listing = this.listings.find(l => l.id === listingId);
                if (listing && this.onAddToCalendar) {
                    this.onAddToCalendar(listing);
                }
            });
        });
        // Attach sort dropdown listener (debounced)
        const sortDropdown = this.container.querySelector('#listings-sort');
        if (sortDropdown) {
            sortDropdown.addEventListener('change', (e) => {
                e.stopPropagation();
                this.sortOption = e.target.value;
                this.debouncedUpdateBody();
            });
        }
        // Attach section header click listeners (for expand/collapse)
        const sectionHeaders = this.container.querySelectorAll('.listings-section-header:not(.all-listings)');
        sectionHeaders.forEach((header) => {
            header.addEventListener('click', (e) => {
                // Only toggle on chevron or header click, not on buttons
                if (e.target.closest('button')) {
                    return;
                }
                const sectionId = header.dataset.section;
                if (sectionId) {
                    this.toggleSectionExpand(sectionId);
                }
            });
        });
        // Attach neighborhood header click listeners
        const headers = this.container.querySelectorAll('.listings-group-header');
        headers.forEach((header) => {
            const neighborhoodId = header.dataset.neighborhoodId;
            const neighborhoodName = header.dataset.neighborhoodName;
            // Click on chevron or name area toggles expand
            const leftArea = header.querySelector('.listings-group-header-left');
            leftArea?.addEventListener('click', (e) => {
                e.stopPropagation();
                if (neighborhoodId) {
                    this.toggleNeighborhoodExpand(neighborhoodId);
                }
            });
            // Click on name (text) opens neighborhood modal
            const nameEl = header.querySelector('.listings-group-name');
            nameEl?.addEventListener('click', (e) => {
                e.stopPropagation();
                if (neighborhoodId && neighborhoodId !== '__other__' && this.onNeighborhoodHeaderClick) {
                    this.onNeighborhoodHeaderClick(neighborhoodId, neighborhoodName || '');
                }
            });
        });
        // Attach neighborhood heart/bell button click listeners (in group headers)
        const nhHeartBtns = this.container.querySelectorAll('.listings-group-header .listings-nh-heart');
        nhHeartBtns.forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const neighborhoodId = btn.dataset.neighborhoodId;
                if (neighborhoodId && this.onNeighborhoodFavoriteToggle) {
                    this.onNeighborhoodFavoriteToggle(neighborhoodId);
                }
            });
        });
        const nhBellBtns = this.container.querySelectorAll('.listings-group-header .listings-nh-bell');
        nhBellBtns.forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const neighborhoodId = btn.dataset.neighborhoodId;
                if (neighborhoodId && this.onNeighborhoodAlertToggle) {
                    this.onNeighborhoodAlertToggle(neighborhoodId);
                }
            });
        });
        // Attach "Show All" click listeners (expands neighborhood)
        const showAllBtns = this.container.querySelectorAll('.listings-show-all');
        showAllBtns.forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                // Get neighborhood ID from parent header
                const header = btn.closest('.listings-group-header');
                const neighborhoodId = header?.getAttribute('data-neighborhood-id');
                if (neighborhoodId) {
                    this.neighborhoodExpandState[neighborhoodId] = true;
                    this.updateBody();
                }
            });
        });
        // Attach neighborhood row click listeners (expandable rows in attribute sections)
        const nhRowWrappers = this.container.querySelectorAll('.listings-neighborhood-row-wrapper');
        nhRowWrappers.forEach((wrapper) => {
            const neighborhoodId = wrapper.dataset.neighborhoodId;
            const row = wrapper.querySelector('.listings-neighborhood-row');
            if (!row)
                return;
            // Click on chevron or left side expands/collapses listings
            const leftEl = row.querySelector('.listings-nh-row-left');
            leftEl?.addEventListener('click', (e) => {
                e.stopPropagation();
                if (neighborhoodId) {
                    // Use prefixed key to track attr section expand state separately
                    this.toggleNeighborhoodExpand(`attr:${neighborhoodId}`);
                }
            });
            // Double-click on name opens neighborhood modal
            const nameEl = row.querySelector('.listings-neighborhood-row-name');
            nameEl?.addEventListener('dblclick', (e) => {
                e.stopPropagation();
                if (neighborhoodId && this.onNeighborhoodHeaderClick) {
                    const name = nameEl.textContent || '';
                    this.onNeighborhoodHeaderClick(neighborhoodId, name);
                }
            });
            // Heart button
            const heartBtn = row.querySelector('.listings-nh-heart');
            heartBtn?.addEventListener('click', (e) => {
                e.stopPropagation();
                if (neighborhoodId && this.onNeighborhoodFavoriteToggle) {
                    this.onNeighborhoodFavoriteToggle(neighborhoodId);
                }
            });
            // Bell button
            const bellBtn = row.querySelector('.listings-nh-bell');
            bellBtn?.addEventListener('click', (e) => {
                e.stopPropagation();
                if (neighborhoodId && this.onNeighborhoodAlertToggle) {
                    this.onNeighborhoodAlertToggle(neighborhoodId);
                }
            });
        });
    }
    /**
     * Toggle section expand/collapse state
     */
    toggleSectionExpand(sectionId) {
        this.sectionExpandState[sectionId] = !this.sectionExpandState[sectionId];
        this.updateBody();
    }
    /**
     * Toggle neighborhood expand/collapse state
     */
    toggleNeighborhoodExpand(neighborhoodId) {
        const explicitState = this.neighborhoodExpandState[neighborhoodId];
        // If state is explicitly set, toggle it; otherwise figure out the smart default and toggle from there
        if (explicitState !== undefined) {
            this.neighborhoodExpandState[neighborhoodId] = !explicitState;
        }
        else {
            // Smart default: collapsed when many neighborhoods (>3)
            const grouped = this.groupListingsByNeighborhood(this.listings);
            const manyNeighborhoods = grouped.size > 3;
            // Default would be !manyNeighborhoods, so toggling gives us manyNeighborhoods
            this.neighborhoodExpandState[neighborhoodId] = manyNeighborhoods;
        }
        this.updateBody();
    }
    setListings(listings) {
        this.listings = listings;
        this.updateBody();
    }
    /**
     * Re-render to reflect a changed container width (drawer opened or
     * resized) - needed because a collapsed neighborhood group's preview
     * card count is computed from the container's current width (see
     * computeColumnCount), not recalculated on its own. Debounced since a
     * resize drag fires continuously.
     */
    refreshLayout() {
        this.debouncedUpdateBody();
    }
    /**
     * Show/hide the drawer's own loading state, independent of the map's
     * bubbles (which render from a separate, faster data source and are
     * never gated on this). Setting listings via setListings() while loading
     * is true has no visible effect until setLoading(false) is also called -
     * callers should set listings first, then clear loading.
     */
    setLoading(loading) {
        if (this.loading === loading)
            return;
        this.loading = loading;
        this.updateBody();
    }
    /**
     * Update favorites set and refresh UI
     * Uses selective DOM updates when possible for better performance
     */
    setFavorites(favoriteIds) {
        const changedIds = this.getChangedIds(this.previousFavoriteIds, favoriteIds);
        this.previousFavoriteIds = new Set(favoriteIds);
        this.favoriteIds = favoriteIds;
        // Try selective update first
        if (changedIds.size > 0 && changedIds.size <= 10) {
            const didSelectiveUpdate = this.updateCardIcons(changedIds, 'favorite');
            if (didSelectiveUpdate) {
                return; // Success - no full rebuild needed
            }
        }
        // Fall back to full rebuild
        this.updateBody();
    }
    /**
     * Update alerts set and refresh UI
     * Uses selective DOM updates when possible for better performance
     */
    setAlerts(alertIds) {
        const changedIds = this.getChangedIds(this.previousAlertIds, alertIds);
        this.previousAlertIds = new Set(alertIds);
        this.alertIds = alertIds;
        // Try selective update first
        if (changedIds.size > 0 && changedIds.size <= 10) {
            const didSelectiveUpdate = this.updateCardIcons(changedIds, 'alert');
            if (didSelectiveUpdate) {
                return; // Success - no full rebuild needed
            }
        }
        // Fall back to full rebuild
        this.updateBody();
    }
    /**
     * Update the Agent Picks id-set and refresh UI. No per-card icon to
     * selectively patch (unlike favorites/alerts - there's no toggle button
     * for this one), so this always does a full rebuild; call sites (one
     * per page load / new ?story= link, not per-keystroke) don't need the
     * selective-update optimization those get.
     */
    setAgentPicks(agentPickIds) {
        this.agentPickIds = agentPickIds;
        this.updateBody();
    }
    /** Same shape as setAgentPicks() above, for the Featured id-set. */
    setFeatured(featuredIds) {
        this.featuredIds = featuredIds;
        this.updateBody();
    }
    /**
     * Update active attributes and refresh UI (debounced)
     */
    setActiveAttributes(attributes) {
        this.activeAttributes = { ...attributes };
        this.debouncedUpdateBody();
    }
    /**
     * Enable or disable grouping by neighborhood (deprecated - use sort option instead)
     */
    setGroupByNeighborhood(enabled) {
        // For backwards compatibility, switch to neighborhood sort when enabled
        if (enabled && this.sortOption !== 'neighborhood') {
            this.sortOption = 'neighborhood';
            this.updateBody();
        }
    }
    /**
     * Check if grouping by neighborhood is enabled
     */
    isGroupedByNeighborhood() {
        return this.sortOption === 'neighborhood';
    }
    /**
     * Set whether filters are active (shows indicator in header)
     */
    setFiltersActive(active) {
        if (this.filtersActive !== active) {
            this.filtersActive = active;
            this.debouncedUpdateBody();
        }
    }
}
//# sourceMappingURL=ListingsDrawer.js.map